import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/blogs`);
      setBlogs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setLoading(false);
    }
  };

  const handleNew = () => {
    setCurrentBlog({
      title: '',
      excerpt: '',
      content: '',
      featured_image: '',
      author: 'Reagle Team',
      category: '',
      tags: [],
      published: true,
      meta_title: '',
      meta_description: '',
      meta_keywords: ''
    });
    setEditMode(true);
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      if (currentBlog.blog_id) {
        // Update existing
        await axios.put(`${BACKEND_URL}/api/cms/blogs/${currentBlog.blog_id}`, currentBlog);
        toast({
          title: "Success",
          description: "Blog updated successfully",
        });
      } else {
        // Create new
        await axios.post(`${BACKEND_URL}/api/cms/blogs`, currentBlog);
        toast({
          title: "Success",
          description: "Blog created successfully",
        });
      }
      setEditMode(false);
      setCurrentBlog(null);
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast({
        title: "Error",
        description: "Failed to save blog",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/cms/blogs/${blogId}`);
      toast({
        title: "Success",
        description: "Blog deleted successfully",
      });
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setCurrentBlog(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (editMode) {
    return (
      <div className="p-8 max-w-5xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-light text-[#2d2d2d]">
            {currentBlog.blog_id ? 'Edit Blog' : 'New Blog'}
          </h1>
          <div className="flex gap-2">
            <Button onClick={handleSave} className="bg-[#0A1A50] hover:bg-[#060F2E] text-white">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
            <Button onClick={handleCancel} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-light text-[#2d2d2d] mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Title *</label>
                <Input
                  value={currentBlog.title}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, title: e.target.value })}
                  placeholder="Enter blog title"
                />
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Excerpt (Short Description) *</label>
                <Textarea
                  value={currentBlog.excerpt}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, excerpt: e.target.value })}
                  placeholder="Short description for blog listing (150-200 characters recommended)"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Content *</label>
                <Textarea
                  value={currentBlog.content}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, content: e.target.value })}
                  placeholder="Full blog content (HTML supported)"
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Author</label>
                  <Input
                    value={currentBlog.author}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, author: e.target.value })}
                    placeholder="Author name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Category</label>
                  <Input
                    value={currentBlog.category || ''}
                    onChange={(e) => setCurrentBlog({ ...currentBlog, category: e.target.value })}
                    placeholder="e.g., Real Estate, Development"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">Featured Image URL</label>
                <Input
                  value={currentBlog.featured_image || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, featured_image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
                  Tags (comma-separated)
                </label>
                <Input
                  value={Array.isArray(currentBlog.tags) ? currentBlog.tags.join(', ') : ''}
                  onChange={(e) => setCurrentBlog({ 
                    ...currentBlog, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="real estate, investment, development"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentBlog.published}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm text-[#2d2d2d]">Published (visible on website)</label>
              </div>
            </div>
          </div>

          {/* SEO Section */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-light text-[#2d2d2d] mb-4">SEO Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
                  Meta Title (Leave empty to use blog title)
                </label>
                <Input
                  value={currentBlog.meta_title || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, meta_title: e.target.value })}
                  placeholder="SEO title for search engines"
                />
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
                  Meta Description
                </label>
                <Textarea
                  value={currentBlog.meta_description || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, meta_description: e.target.value })}
                  placeholder="SEO description (150-160 characters recommended)"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-normal text-[#2d2d2d] mb-2">
                  Meta Keywords (comma-separated)
                </label>
                <Input
                  value={currentBlog.meta_keywords || ''}
                  onChange={(e) => setCurrentBlog({ ...currentBlog, meta_keywords: e.target.value })}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Blog Manager</h1>
        <Button onClick={handleNew} className="bg-[#0A1A50] hover:bg-[#060F2E] text-white">
          <Plus className="w-4 h-4 mr-2" />
          New Blog
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {blogs.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-[#2d2d2d]">No blogs yet. Create your first blog!</p>
          </div>
        ) : (
          blogs.map((blog) => (
            <div key={blog.blog_id} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-normal text-[#2d2d2d]">{blog.title}</h3>
                    {blog.published ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Published
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Draft
                      </span>
                    )}
                  </div>
                  
                  <p className="text-[#2d2d2d] text-sm mb-3 line-clamp-2">{blog.excerpt}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-[#2d2d2d]">
                    <span>By {blog.author}</span>
                    {blog.category && <span>• {blog.category}</span>}
                    <span>• {formatDate(blog.created_at)}</span>
                    <span>• {blog.views} views</span>
                  </div>

                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {blog.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-[#2d2d2d] text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ml-4 flex gap-2">
                  <Button
                    onClick={() => handleEdit(blog)}
                    size="sm"
                    variant="outline"
                    className="text-[#0A1A50] border-[#0A1A50]"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(blog.blog_id)}
                    size="sm"
                    variant="destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogManager;
